import { Injectable } from '@angular/core';
import moment from 'moment';

import { MarvDateService } from '@marv/services';
import { ApiService, PhotoService } from '@app/services';
import { 
    Operation, OperationCard, OperationCustomerPlanningSetting, PermanentOperation, PermanentAgent, CustomerOrderQuota 
} from '@app/models';

@Injectable({
  providedIn: 'root'
})
export class OperationService {
    
    static Operations: Array<OperationCard> = [];
    static OperationsSettings: Map<number, OperationCustomerPlanningSetting> = new Map<number, OperationCustomerPlanningSetting>;

    constructor(
        private apiService: ApiService,
        private photoService: PhotoService
    ) {}
    
    async init(){        
        let start = moment();
        start.subtract(1, 'year');
        let end = moment();
        end.add(1, 'year');
        
        OperationService.Operations = await this.loadOperationsCards(start.toDate(), end.toDate()); 
        for(let op of OperationService.Operations){
            if(op.type === 'loga'){
                let settingsOP = await this.loadOperationCustomerPlanningSettings(op.id);
                if(settingsOP){
                    OperationService.OperationsSettings.set(op.id, settingsOP);
                }                
            }
            else if (op.type === 'lgg') {
                op.operationLogoUrl = await this.photoService.loadPearlsLogo(op.id);
            }
        }        
    }
    
    reset(){      
        OperationService.Operations = [];
        OperationService.OperationsSettings = new Map<number, OperationCustomerPlanningSetting>;        
    }
    
    async loadOperation(operationId: number): Promise<Operation|null> {
        let operation: Operation|null = null;
        let response = await this.apiService.get('operation/' + operationId + '/light/with-quota');
        if(response?.data){
            operation = Operation.FromJson(response.data);
        }
        return operation;        
    }
    
    async loadOperationCustomerPlanningSettings(operationId: number): Promise<OperationCustomerPlanningSetting|null> {
        let settings: OperationCustomerPlanningSetting|null = null;
        let response = await this.apiService.get('operation/' + operationId + '/customer-planification-settings');
        if(response?.data){
            settings = new OperationCustomerPlanningSetting().setup(response?.data);            
        }
        return settings;
    } 

    async loadOperationsWithQuota(active: boolean = true, openForPlanningOnly: boolean = false): Promise<Operation[]> {
        let operations: Array<Operation> = [];
        let params = {
            opActive: (active === true ? "true" : "false"),
            openForPlanningOnly: (openForPlanningOnly === true ? "true" : "false")
        };
        let response = await this.apiService.get('operation/with-quotas', params);
        if(response?.data){
            for(let o of response.data){
                operations.push(Operation.FromJson(o));
            }
            
            operations.sort(((a, b): number => {
                return (a.name > b.name) ? 1 : -1;
            }));
        }
        return operations;
    }
    
    async loadOperationsCards(startDate?: Date, endDate?: Date): Promise<OperationCard[]> {
        let operations: Array<OperationCard> = [];
        let params: any = {};
        if(startDate){
            params.from = MarvDateService.FromDate(startDate);
        }
        if(endDate){
            params.to = MarvDateService.FromDate(endDate);
        }
        let response = await this.apiService.get('operation/mobile-cards', params);
        if(response?.data){
            for(let o of response.data){
                operations.push(OperationCard.FromJson(o));
            }
            
            operations.sort(((a, b): number => {
                //sort by planning closing time first
                let dA = a.planningOpenUntil == null ? 99999 : a.planningOpenUntil;
                let dB = b.planningOpenUntil == null ? 99999 : b.planningOpenUntil;
                if (dA != dB) return dB - dA;

                //sort by activity closing time first
                dA = a.earliestPlannableDate == null ? 99999 : a.earliestPlannableDate;
                dB = b.earliestPlannableDate == null ? 99999 : b.earliestPlannableDate;
                if (dA != dB) return dB - dA;

                // sort by name
                return a.name.localeCompare(b.name);
          }));
            
        }
        return operations;
    }
    
    async loadPermanentsOperations(): Promise<PermanentOperation[]>{
        let permanentsOperations: Array<PermanentOperation> = [];
        let response = await this.apiService.get('lgg-contexts');
        if(response?.data){
            for(let o of response.data){
                let op = PermanentOperation.FromJson(o);
                op.logo = await this.photoService.loadPearlsLogo(op.id);
                permanentsOperations.push(op);
            }
        }
        return permanentsOperations;
    }
    
    async loadHourlyQuotaStatus(sector: number, profil: number): Promise<CustomerOrderQuota|null> {
        let customerOrderQuota: CustomerOrderQuota|null = null;
        let response = await this.apiService.get('order/customer-sector/' + sector + '/profil/' + profil + '/quota');
        if(response?.data){
            customerOrderQuota = response.data;
        }
        return customerOrderQuota;        
    }
}
